/**
 * EBM Validation Utilities
 * Validates data according to RRA EBM API requirements
 */

/**
 * Check if a value can be parsed to a valid number
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value can be parsed to a valid number
 */
function isValidNumber(value) {
  if (typeof value === "number") {
    return !isNaN(value) && isFinite(value);
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return !isNaN(parsed) && isFinite(parsed);
  }
  return false;
}

/**
 * Parse a value to a number, handling both number and string types
 * @param {any} value - The value to parse
 * @returns {number} - The parsed number
 */
function parseToNumber(value) {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    return parseFloat(value);
  }
  return NaN;
}

export class EbmValidationError extends Error {
  constructor(field, message, value = null) {
    super(`${field}: ${message}`);
    this.field = field;
    this.value = value;
    this.name = "EbmValidationError";
  }
}

/**
 * Validates ItemSaveReq data structure
 */
export class ItemSaveReqValidator {
  static validate(data, context = {}) {
    const errors = [];
    const { hasExistingEbmMetadata = true } = context;

    // Required CHAR fields validation
    const requiredCharFields = [
      { field: "tin", maxLen: 9, name: "TIN" },
      { field: "bhfId", maxLen: 2, name: "Branch ID" },
      {
        field: "itemCd",
        maxLen: 20,
        name: "Item Code",
        optionalWhenNoMetadata: true,
      },
      { field: "itemClsCd", maxLen: 10, name: "Item Classification Code" },
      {
        field: "itemTyCd",
        maxLen: 5,
        name: "Item Type Code",
        optionalWhenNoMetadata: true,
      },
      { field: "itemNm", maxLen: 200, name: "Item Name" },
      {
        field: "orgnNatCd",
        maxLen: 5,
        name: "Origin Place Code",
        optionalWhenNoMetadata: true,
      },
      {
        field: "pkgUnitCd",
        maxLen: 5,
        name: "Packaging Unit Code",
        optionalWhenNoMetadata: true,
      },
      {
        field: "qtyUnitCd",
        maxLen: 5,
        name: "Quantity Unit Code",
        optionalWhenNoMetadata: true,
      },
      { field: "taxTyCd", maxLen: 5, name: "Taxation Type Code" },
      { field: "isrcAplcbYn", maxLen: 1, name: "Insurance Applicability" },
      { field: "useYn", maxLen: 1, name: "Used/Unused flag" },
    ];

    requiredCharFields.forEach(
      ({ field, maxLen, name, optionalWhenNoMetadata = false }) => {
        const value = data[field];

        // Skip validation for optional fields when no EBM metadata exists
        if (optionalWhenNoMetadata && !hasExistingEbmMetadata) {
          return;
        }

        if (!value || typeof value !== "string" || value.length > maxLen) {
          errors.push(
            new EbmValidationError(
              field,
              `${name} should be defined and not exceed ${maxLen} characters`,
              value
            )
          );
        }
      }
    );

    // Required NUMBER fields validation
    if (!data.dftPrc || !isValidNumber(data.dftPrc)) {
      errors.push(
        new EbmValidationError(
          "dftPrc",
          "Default Unit Price should be a valid number",
          data.dftPrc
        )
      );
    }

    // Optional CHAR fields validation
    const optionalCharFields = [
      { field: "itemStdNm", maxLen: 200, name: "Item Standard Name" },
      { field: "btchNo", maxLen: 10, name: "Batch Number" },
      { field: "bcd", maxLen: 20, name: "Barcode" },
      { field: "addInfo", maxLen: 7, name: "Additional Information" },
    ];

    optionalCharFields.forEach(({ field, maxLen, name }) => {
      const value = data[field];
      if (
        value !== null &&
        value !== undefined &&
        (typeof value !== "string" || value.length > maxLen)
      ) {
        errors.push(
          new EbmValidationError(
            field,
            `${name} should not exceed ${maxLen} characters`,
            value
          )
        );
      }
    });

    // Optional NUMBER fields validation
    const optionalNumberFields = [
      { field: "grpPrcL1", name: "Group1 Unit Price" },
      { field: "grpPrcL2", name: "Group2 Unit Price" },
      { field: "grpPrcL3", name: "Group3 Unit Price" },
      { field: "grpPrcL4", name: "Group4 Unit Price" },
      { field: "grpPrcL5", name: "Group5 Unit Price" },
      { field: "sftyQty", name: "Safety Quantity" },
    ];

    optionalNumberFields.forEach(({ field, name }) => {
      const value = data[field];
      if (value !== null && value !== undefined && !isValidNumber(value)) {
        errors.push(
          new EbmValidationError(
            field,
            `${name} should be a valid number`,
            value
          )
        );
      }
    });

    return errors;
  }

  static getDefaultValues() {
    return {
      tin: "",
      bhfId: "",
      itemClsCd: "",
      itemCd: "",
      itemTyCd: "",
      itemNm: "",
      orgnNatCd: "RW",
      pkgUnitCd: "NT",
      qtyUnitCd: "U",
      taxTyCd: "B",
      dftPrc: 0,
      grpPrcL1: 0,
      grpPrcL2: 0,
      grpPrcL3: 0,
      grpPrcL4: 0,
      grpPrcL5: 0,
      sftyQty: 0,
      isrcAplcbYn: "N",
      useYn: "Y",
      itemStdNm: null,
      btchNo: null,
      bcd: null,
      addInfo: null,
      rraModYn: "N",
      regBhfId: null,
      regrNm: "",
      regrId: "",
      modrNm: "",
      modrId: "",
    };
  }
}

/**
 * Validates TrnsPurchaseSaveReq data structure
 */
export class TrnsPurchaseSaveReqValidator {
  static validate(data) {
    const errors = [];

    // Required string fields
    const requiredStringFields = [
      { field: "tin", maxLen: 9, name: "TIN" },
      { field: "bhfId", maxLen: 2, name: "Branch ID" },
      { field: "regTyCd", maxLen: 5, name: "Registration Type Code" },
      { field: "pchsTyCd", maxLen: 5, name: "Purchase Type Code" },
      { field: "rcptTyCd", maxLen: 5, name: "Receipt Type Code" },
      { field: "pmtTyCd", maxLen: 5, name: "Payment Type Code" },
      { field: "pchsSttsCd", maxLen: 5, name: "Purchase Status Code" },
      { field: "pchsDt", maxLen: 8, name: "Purchase Date" },
      { field: "regrNm", maxLen: 60, name: "Registrant Name" },
      { field: "regrId", maxLen: 20, name: "Registrant ID" },
      { field: "modrNm", maxLen: 60, name: "Modifier Name" },
      { field: "modrId", maxLen: 20, name: "Modifier ID" },
    ];

    requiredStringFields.forEach(({ field, maxLen, name }) => {
      const value = data[field];
      if (!value || typeof value !== "string" || value.length > maxLen) {
        errors.push(
          new EbmValidationError(
            field,
            `${name} should be defined and not exceed ${maxLen} characters`,
            value
          )
        );
      }
    });

    // Required integer fields
    const requiredIntFields = [
      { field: "invcNo", name: "Invoice Number" },
      { field: "orgInvcNo", name: "Original Invoice Number" },
      { field: "totItemCnt", name: "Total Item Count", min: 0 },
    ];

    requiredIntFields.forEach(({ field, name, min }) => {
      const value = data[field];
      if (!isValidNumber(value)) {
        errors.push(
          new EbmValidationError(
            field,
            `${name} should be a valid number`,
            value
          )
        );
      } else {
        const numValue = parseToNumber(value);
        if (
          !Number.isInteger(numValue) ||
          (min !== undefined && numValue < min)
        ) {
          errors.push(
            new EbmValidationError(
              field,
              `${name} should be a valid integer${
                min !== undefined ? ` >= ${min}` : ""
              }`,
              value
            )
          );
        }
      }
    });

    // Required number fields (can be float)
    const requiredNumberFields = [
      "taxblAmtA",
      "taxblAmtB",
      "taxblAmtC",
      "taxblAmtD",
      "taxAmtA",
      "taxAmtB",
      "taxAmtC",
      "taxAmtD",
      "totTaxblAmt",
      "totTaxAmt",
      "totAmt",
    ];

    requiredNumberFields.forEach((field) => {
      const value = data[field];
      if (!isValidNumber(value)) {
        errors.push(
          new EbmValidationError(
            field,
            `${field} should be a valid number`,
            value
          )
        );
      } else {
        const numValue = parseToNumber(value);
        if (numValue < 0) {
          errors.push(
            new EbmValidationError(
              field,
              `${field} should be a non-negative number`,
              value
            )
          );
        }
      }
    });

    // Tax rate validation (0-100 or 0-1)
    const taxRateFields = ["taxRtA", "taxRtB", "taxRtC", "taxRtD"];
    taxRateFields.forEach((field) => {
      const value = data[field];
      if (!isValidNumber(value)) {
        errors.push(
          new EbmValidationError(
            field,
            `${field} should be a valid number`,
            value
          )
        );
      } else {
        const numValue = parseToNumber(value);
        if (numValue < 0 || numValue > 100) {
          errors.push(
            new EbmValidationError(
              field,
              `${field} should be between 0 and 100`,
              value
            )
          );
        }
      }
    });

    // Supplier validation (required)
    const supplierFields = [
      { field: "spplrTin", minLen: 5, name: "Supplier TIN" },
      { field: "spplrBhfId", maxLen: 2, name: "Supplier Branch ID" },
      { field: "spplrNm", maxLen: 60, name: "Supplier Name" },
    ];

    supplierFields.forEach(({ field, minLen, maxLen, name }) => {
      const value = data[field];
      if (
        !value ||
        typeof value !== "string" ||
        (minLen && value.length < minLen) ||
        (maxLen && value.length > maxLen)
      ) {
        errors.push(
          new EbmValidationError(
            field,
            `${name} should be defined${
              minLen ? ` (min ${minLen} chars)` : ""
            }${maxLen ? ` (max ${maxLen} chars)` : ""}`,
            value
          )
        );
      }
    });

    // Supplier invoice number validation
    if (!isValidNumber(data.spplrInvcNo)) {
      errors.push(
        new EbmValidationError(
          "spplrInvcNo",
          "Supplier Invoice Number should be a valid number",
          data.spplrInvcNo
        )
      );
    } else {
      const numValue = parseToNumber(data.spplrInvcNo);
      if (!Number.isInteger(numValue)) {
        errors.push(
          new EbmValidationError(
            "spplrInvcNo",
            "Supplier Invoice Number should be an integer",
            data.spplrInvcNo
          )
        );
      }
    }

    // Item list validation
    if (!Array.isArray(data.itemList) || data.itemList.length === 0) {
      errors.push(
        new EbmValidationError(
          "itemList",
          "Item List should be a non-empty array",
          data.itemList
        )
      );
    }

    return errors;
  }

  static getDefaultValues() {
    return {
      tin: "",
      bhfId: "",
      invcNo: 0,
      orgInvcNo: 0,
      regTyCd: "A",
      pchsTyCd: "N",
      rcptTyCd: "P",
      pmtTyCd: "01",
      pchsSttsCd: "02",
      pchsDt: "",
      totItemCnt: 0,
      taxblAmtA: 0,
      taxblAmtB: 0,
      taxblAmtC: 0,
      taxblAmtD: 0,
      taxRtA: 0,
      taxRtB: 18,
      taxRtC: 0,
      taxRtD: 0,
      taxAmtA: 0,
      taxAmtB: 0,
      taxAmtC: 0,
      taxAmtD: 0,
      totTaxblAmt: 0,
      totTaxAmt: 0,
      totAmt: 0,
      regrNm: "",
      regrId: "",
      modrNm: "",
      modrId: "",
      itemList: [],
      spplrTin: "",
      spplrBhfId: "",
      spplrNm: "",
      spplrInvcNo: 0,
      spplrSdcId: null,
      spplrItemCd: null,
      cfmDt: null,
      wrhsDt: null,
      cnclReqDt: null,
      cnclDt: null,
      rfdDt: null,
      remark: null,
    };
  }
}

/**
 * EBM Field Definitions for UI rendering
 */
export const EBM_FIELD_DEFINITIONS = {
  itemSave: {
    essential: [
      {
        key: "itemNm",
        label: "Item Name",
        type: "text",
        required: true,
        maxLength: 200,
      },
      {
        key: "itemCd",
        label: "Item Code",
        type: "text",
        required: true,
        maxLength: 20,
      },
      {
        key: "dftPrc",
        label: "Sale Unit Price",
        type: "number",
        required: true,
        min: 0,
      },
      {
        key: "taxTyCd",
        label: "Tax Type",
        type: "select",
        required: true,
        options: [
          { value: "A", label: "A - Exempt" },
          { value: "B", label: "B - 18% VAT" },
          { value: "C", label: "C - 0% VAT" },
          { value: "D", label: "D - Special" },
        ],
      },
      {
        key: "qtyUnitCd",
        label: "Quantity Unit",
        type: "select",
        required: true,
        options: [
          { value: "U", label: "U - Unit" },
          { value: "KG", label: "KG - Kilogram" },
          { value: "L", label: "L - Liter" },
          { value: "M", label: "M - Meter" },
        ],
      },
    ],
    advanced: [
      {
        key: "itemClsCd",
        label: "Item Classification",
        type: "text",
        required: true,
        maxLength: 10,
      },
      {
        key: "itemTyCd",
        label: "Item Type",
        type: "select",
        required: true,
        options: [
          { value: "1", label: "1 - Raw Material" },
          { value: "2", label: "2 - Finished Good" },
          { value: "3", label: "3 - Service" },
        ],
      },
      {
        key: "orgnNatCd",
        label: "Origin Country",
        type: "text",
        required: true,
        maxLength: 5,
        default: "RW",
      },
      {
        key: "pkgUnitCd",
        label: "Package Unit",
        type: "select",
        required: true,
        options: [
          { value: "NT", label: "NT - No Package" },
          { value: "BX", label: "BX - Box" },
          { value: "PK", label: "PK - Pack" },
        ],
      },
      { key: "bcd", label: "Barcode", type: "text", maxLength: 20 },
      {
        key: "itemStdNm",
        label: "Standard Name",
        type: "text",
        maxLength: 200,
      },
      { key: "grpPrcL1", label: "Group Price 1", type: "number", min: 0 },
      { key: "grpPrcL2", label: "Group Price 2", type: "number", min: 0 },
      { key: "sftyQty", label: "Safety Quantity", type: "number", min: 0 },
      {
        key: "isrcAplcbYn",
        label: "Insurance Applicable",
        type: "select",
        required: true,
        options: [
          { value: "Y", label: "Yes" },
          { value: "N", label: "No" },
        ],
      },
      {
        key: "useYn",
        label: "In Use",
        type: "select",
        required: true,
        options: [
          { value: "Y", label: "Yes" },
          { value: "N", label: "No" },
        ],
      },
    ],
  },
};

/**
 * Validates StoreProduct PATCH data structure
 */
export class StoreProductValidator {
  static validate(data) {
    const errors = [];

    // Required fields validation
    if (!data.product_id) {
      errors.push(
        new EbmValidationError(
          "product_id",
          "Product ID is required",
          data.product_id
        )
      );
    }

    if (
      !data.name ||
      typeof data.name !== "string" ||
      data.name.trim().length === 0
    ) {
      errors.push(
        new EbmValidationError(
          "name",
          "Product name is required and must be a non-empty string",
          data.name
        )
      );
    }

    // Optional fields validation
    if (data.item_sku !== undefined) {
      if (typeof data.item_sku !== "string") {
        errors.push(
          new EbmValidationError(
            "item_sku",
            "SKU must be a string",
            data.item_sku
          )
        );
      } else if (data.item_sku && data.item_sku.length > 16) {
        errors.push(
          new EbmValidationError(
            "item_sku",
            "SKU cannot exceed 16 characters",
            data.item_sku
          )
        );
      } else if (data.item_sku && !data.item_sku.match(/^[A-Z0-9]+$/)) {
        errors.push(
          new EbmValidationError(
            "item_sku",
            "SKU must contain only alphanumeric characters",
            data.item_sku
          )
        );
      }
    }

    if (
      data.barcode !== undefined &&
      data.barcode &&
      typeof data.barcode !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "barcode",
          "Barcode must be a string",
          data.barcode
        )
      );
    }

    if (
      data.description !== undefined &&
      data.description &&
      typeof data.description !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "description",
          "Description must be a string",
          data.description
        )
      );
    }

    if (
      data.quantity_unit !== undefined &&
      data.quantity_unit &&
      typeof data.quantity_unit !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "quantity_unit",
          "Quantity unit must be a string",
          data.quantity_unit
        )
      );
    }

    if (
      data.default_quantity_unit !== undefined &&
      data.default_quantity_unit &&
      typeof data.default_quantity_unit !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "default_quantity_unit",
          "Default quantity unit must be a string",
          data.default_quantity_unit
        )
      );
    }

    // RRA EBM metadata validation
    if (data.rra_ebm_metadata !== undefined) {
      const metadataErrors = this.validateRraEbmMetadata(data.rra_ebm_metadata);
      errors.push(...metadataErrors);
    }

    return errors;
  }

  static validateRraEbmMetadata(metadata) {
    const errors = [];

    if (metadata && typeof metadata === "object") {
      if (metadata.itemCd && typeof metadata.itemCd !== "string") {
        errors.push(
          new EbmValidationError(
            "rra_ebm_metadata.itemCd",
            "Item Code must be a string",
            metadata.itemCd
          )
        );
      }

      if (metadata.taxTyCd && typeof metadata.taxTyCd !== "string") {
        errors.push(
          new EbmValidationError(
            "rra_ebm_metadata.taxTyCd",
            "Tax Type Code must be a string",
            metadata.taxTyCd
          )
        );
      }

      if (metadata.itemClsCd && typeof metadata.itemClsCd !== "string") {
        errors.push(
          new EbmValidationError(
            "rra_ebm_metadata.itemClsCd",
            "Item Classification Code must be a string",
            metadata.itemClsCd
          )
        );
      }
    }

    return errors;
  }

  static getDefaultValues() {
    return {
      product_id: null,
      name: "",
      item_sku: null,
      barcode: null,
      description: null,
      quantity_unit: "U",
      default_quantity_unit: "U",
      rra_ebm_metadata: {
        itemCd: null,
        taxTyCd: "B",
        itemClsCd: null,
      },
    };
  }
}

/**
 * Validates StoreProductPurchase PATCH data structure
 */
export class StoreProductPurchaseValidator {
  static validate(data) {
    const errors = [];

    // Required fields validation
    if (!data.product_purchase_id) {
      errors.push(
        new EbmValidationError(
          "product_purchase_id",
          "Product Purchase ID is required",
          data.product_purchase_id
        )
      );
    }

    if (data.quantity === undefined || data.quantity === null) {
      errors.push(
        new EbmValidationError(
          "quantity",
          "Quantity is required",
          data.quantity
        )
      );
    } else if (!isValidNumber(data.quantity)) {
      errors.push(
        new EbmValidationError(
          "quantity",
          "Quantity must be a valid number",
          data.quantity
        )
      );
    } else {
      const numValue = parseToNumber(data.quantity);
      if (numValue < 0) {
        errors.push(
          new EbmValidationError(
            "quantity",
            "Quantity must be a non-negative number",
            data.quantity
          )
        );
      }
    }

    if (data.unit_price === undefined || data.unit_price === null) {
      errors.push(
        new EbmValidationError(
          "unit_price",
          "Unit price is required",
          data.unit_price
        )
      );
    } else if (!isValidNumber(data.unit_price)) {
      errors.push(
        new EbmValidationError(
          "unit_price",
          "Unit price must be a valid number",
          data.unit_price
        )
      );
    } else {
      const numValue = parseToNumber(data.unit_price);
      if (numValue < 0) {
        errors.push(
          new EbmValidationError(
            "unit_price",
            "Unit price must be a non-negative number",
            data.unit_price
          )
        );
      }
    }

    if (!data.currency || typeof data.currency !== "string") {
      errors.push(
        new EbmValidationError(
          "currency",
          "Currency is required and must be a string",
          data.currency
        )
      );
    }

    // Optional fields validation
    if (
      data.expiration_date !== undefined &&
      data.expiration_date &&
      typeof data.expiration_date !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "expiration_date",
          "Expiration date must be a string",
          data.expiration_date
        )
      );
    }

    if (
      data.payment_method_id !== undefined &&
      data.payment_method_id &&
      !isValidNumber(data.payment_method_id)
    ) {
      errors.push(
        new EbmValidationError(
          "payment_method_id",
          "Payment method ID must be a valid number",
          data.payment_method_id
        )
      );
    }

    if (
      data.created_at !== undefined &&
      data.created_at &&
      typeof data.created_at !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "created_at",
          "Created at must be a string",
          data.created_at
        )
      );
    }

    if (
      data.display_quantity_unit !== undefined &&
      data.display_quantity_unit &&
      typeof data.display_quantity_unit !== "string"
    ) {
      errors.push(
        new EbmValidationError(
          "display_quantity_unit",
          "Display quantity unit must be a string",
          data.display_quantity_unit
        )
      );
    }

    // RRA EBM metadata validation
    if (data.rra_ebm_metadata !== undefined) {
      const metadataErrors = this.validateRraEbmMetadata(data.rra_ebm_metadata);
      errors.push(...metadataErrors);
    }

    return errors;
  }

  static validateRraEbmMetadata(metadata) {
    const errors = [];

    if (metadata && typeof metadata === "object") {
      if (
        metadata.import_metadata &&
        typeof metadata.import_metadata !== "object"
      ) {
        errors.push(
          new EbmValidationError(
            "rra_ebm_metadata.import_metadata",
            "Import metadata must be an object",
            metadata.import_metadata
          )
        );
      }
    }

    return errors;
  }

  static getDefaultValues() {
    return {
      product_purchase_id: null,
      quantity: 0,
      unit_price: 0,
      currency: "RWF",
      expiration_date: null,
      payment_method_id: null,
      created_at: null,
      display_quantity_unit: null,
      rra_ebm_metadata: {
        import_metadata: null,
      },
    };
  }
}

/**
 * Utility functions for data transformation
 */
export const EbmDataUtils = {
  /**
   * Transform inventory item to ItemSaveReq format
   */
  inventoryToItemSaveReq(inventoryItem, ebmSettings, userInfo) {
    const product = inventoryItem.product;
    const price = inventoryItem.price;

    return {
      tin: ebmSettings.tin,
      bhfId: ebmSettings.bhf_id,
      itemCd:
        product.rra_ebm_metadata?.itemCd ||
        product.item_sku ||
        `AUTO_${product.id}`,
      itemClsCd: product.rra_ebm_metadata?.itemClsCd || "5020230602",
      itemTyCd: "2", // Finished Good
      itemNm: product.name,
      itemStdNm: product.name,
      orgnNatCd: "RW",
      pkgUnitCd: "NT",
      qtyUnitCd: product.quantity_unit || "U",
      taxTyCd: product.rra_ebm_metadata?.taxTyCd || "B",
      bcd: product.barcode || "",
      dftPrc: price?.unit_price || 0,
      grpPrcL1: price?.unit_price || 0,
      grpPrcL2: price?.unit_price || 0,
      grpPrcL3: price?.unit_price || 0,
      grpPrcL4: price?.unit_price || 0,
      grpPrcL5: price?.unit_price || 0,
      sftyQty: 0,
      isrcAplcbYn: "N",
      useYn: "Y",
      regrNm: userInfo?.names || "System",
      regrId: userInfo?.id?.toString() || "1",
      modrNm: userInfo?.names || "System",
      modrId: userInfo?.id?.toString() || "1",
    };
  },

  /**
   * Transform inventory item to StoreProduct PATCH format
   */
  inventoryToStoreProductPatch(inventoryItem) {
    const product = inventoryItem.product;

    return {
      product_id: product.id,
      name: product.name,
      item_sku: product.item_sku,
      barcode: product.barcode,
      description: product.description,
      quantity_unit: product.quantity_unit,
      default_quantity_unit: product.default_quantity_unit,
      rra_ebm_metadata: product.rra_ebm_metadata,
    };
  },

  /**
   * Transform inventory item to StoreProductPurchase PATCH format
   */
  inventoryToStoreProductPurchasePatch(inventoryItem) {
    const product = inventoryItem.product;
    const price = inventoryItem.price;

    return {
      product_purchase_id: price?.id,
      quantity: parseToNumber(inventoryItem.quantity),
      unit_price: price?.unit_price || 0,
      currency: price?.currency || "RWF",
      expiration_date: null,
      payment_method_id: null,
      created_at: price?.created_at,
      display_quantity_unit: product.default_quantity_unit,
      rra_ebm_metadata: {
        import_metadata: null,
      },
    };
  },

  /**
   * Format date for EBM API (YYYYMMDD)
   */
  formatEbmDate(date) {
    if (!date) return new Date().toISOString().slice(0, 10).replace(/-/g, "");
    return new Date(date).toISOString().slice(0, 10).replace(/-/g, "");
  },

  /**
   * Calculate tax amounts based on taxable amount and rate
   */
  calculateTaxAmount(taxableAmount, taxRate) {
    return Math.round(taxableAmount * (taxRate / 100) * 100) / 100;
  },

  /**
   * Validate and transform form data for API submission
   */
  validateAndTransformFormData(formData, validatorClass) {
    const errors = validatorClass.validate(formData);
    if (errors.length > 0) {
      throw new Error(
        `Validation failed: ${errors.map((e) => e.message).join(", ")}`
      );
    }
    return formData;
  },
};
